import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Textarea,
  Center,
  Image,
  SimpleGrid,
  FileButton,
  Box,
  MultiSelect,
  Checkbox,
  Select,
  TextInput,
  SegmentedControl
} from '@mantine/core';
import { IconArrowLeft, IconCamera, IconX, IconCheck } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useApiClient, useSession } from '../api';
import { uploadProfileImage } from '../api/uploads';

// Contract §1.5 — the Profile `gender` enum.
const GENDER_OPTIONS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'non-binary', label: 'Non-binary' },
  { value: 'transgender-female', label: 'Transgender female' },
  { value: 'transgender-male', label: 'Transgender male' },
  { value: 'prefer-not-to-say', label: 'Prefer not to say' },
];

// All the foot attributes for tags
const footAttributes = [
  // Size
  { value: 'small-feet', label: 'Small Feet' },
  { value: 'large-feet', label: 'Large Feet' },
  { value: 'medium-feet', label: 'Medium Feet' },
  { value: 'petite-feet', label: 'Petite Feet' },
  { value: 'big-feet', label: 'Big Feet' },
  
  // Shape
  { value: 'fat-feet', label: 'Fat Feet' },
  { value: 'thin-feet', label: 'Thin Feet' },
  { value: 'wide-feet', label: 'Wide Feet' },
  { value: 'narrow-feet', label: 'Narrow Feet' },
  { value: 'curvy-feet', label: 'Curvy Feet' },
  
  // Skin Tone
  { value: 'tan-feet', label: 'Tan Feet' },
  { value: 'fair-feet', label: 'Fair Feet' },
  { value: 'dark-feet', label: 'Dark Feet' },
  { value: 'olive-feet', label: 'Olive Feet' },
  { value: 'brown-feet', label: 'Brown Feet' },
  { value: 'pale-feet', label: 'Pale Feet' },
  
  // Toe Shape
  { value: 'long-toes', label: 'Long Toes' },
  { value: 'short-toes', label: 'Short Toes' },
  { value: 'round-toes', label: 'Round Toes' },
  { value: 'square-toes', label: 'Square Toes' },
  { value: 'pointed-toes', label: 'Pointed Toes' },
  { value: 'oval-toes', label: 'Oval Toes' },
  
  // Toe Conditions
  { value: 'missing-toe', label: 'Missing Toe' },
  { value: 'extra-toe', label: 'Extra Toe' },
  { value: 'webbed-toes', label: 'Webbed Toes' },
  { value: 'curled-toes', label: 'Curled Toes' },
  { value: 'bunions', label: 'Bunions' },
  
  // Nails - Polish Colors
  { value: 'red-nail-polish', label: 'Red Nail Polish' },
  { value: 'pink-nail-polish', label: 'Pink Nail Polish' },
  { value: 'purple-nail-polish', label: 'Purple Nail Polish' },
  { value: 'blue-nail-polish', label: 'Blue Nail Polish' },
  { value: 'black-nail-polish', label: 'Black Nail Polish' },
  { value: 'white-nail-polish', label: 'White Nail Polish' },
  { value: 'gold-nail-polish', label: 'Gold Nail Polish' },
  { value: 'silver-nail-polish', label: 'Silver Nail Polish' },
  { value: 'nude-nail-polish', label: 'Nude Nail Polish' },
  { value: 'neon-nail-polish', label: 'Neon Nail Polish' },
  { value: 'glitter-nail-polish', label: 'Glitter Nail Polish' },
  { value: 'matte-nail-polish', label: 'Matte Nail Polish' },
  
  // Nail Art & Designs
  { value: 'french-manicure', label: 'French Manicure' },
  { value: 'gel-nails', label: 'Gel Nails' },
  { value: 'acrylic-nails', label: 'Acrylic Nails' },
  { value: 'nail-art', label: 'Nail Art' },
  { value: 'rhinestones', label: 'Rhinestones' },
  { value: 'charms', label: 'Charms' },
  { value: 'studs', label: 'Studs' },
  { value: 'gradient-ombres', label: 'Gradient/Ombré' },
  { value: 'marble-nails', label: 'Marble Nails' },
  { value: 'chrome-nails', label: 'Chrome Nails' },
  { value: 'floral-nails', label: 'Floral Nails' },
  { value: 'animal-print', label: 'Animal Print' },
  
  // Pedicure Types
  { value: 'spa-pedicure', label: 'Spa Pedicure' },
  { value: 'classic-pedicure', label: 'Classic Pedicure' },
  { value: 'gel-pedicure', label: 'Gel Pedicure' },
  { value: 'acupuncture-pedicure', label: 'Acupuncture Pedicure' },
  { value: 'hot-stone-pedicure', label: 'Hot Stone Pedicure' },
  { value: 'fish-pedicure', label: 'Fish Pedicure' },
  { value: 'paraffin-pedicure', label: 'Paraffin Pedicure' },
  
  // Foot Features
  { value: 'tattoos', label: 'Tattoos' },
  { value: 'piercings', label: 'Piercings' },
  { value: 'foot-jewelry', label: 'Foot Jewelry' },
  { value: 'ankle-bracelets', label: 'Ankle Bracelets' },
  { value: 'toe-rings', label: 'Toe Rings' },
  { value: 'pedicure-extras', label: 'Pedicure Extras' },
  
  // Sole Characteristics
  { value: 'soft-soles', label: 'Soft Soles' },
  { value: 'rough-soles', label: 'Rough Soles' },
  { value: 'calloused-soles', label: 'Calloused Soles' },
  { value: 'smooth-soles', label: 'Smooth Soles' },
  { value: 'ticklish-feet', label: 'Ticklish Feet' },
  { value: 'arched-feet', label: 'Arched Feet' },
  { value: 'flat-feet', label: 'Flat Feet' },
  { value: 'high-arches', label: 'High Arches' },
  
  // Foot Condition
  { value: 'moisturized-feet', label: 'Moisturized Feet' },
  { value: 'dry-feet', label: 'Dry Feet' },
  { value: 'perfumed-feet', label: 'Perfumed Feet' },
  { value: 'fresh-feet', label: 'Fresh Feet' },
  { value: 'dirty-feet', label: 'Dirty Feet' },
  { value: 'sweaty-feet', label: 'Sweaty Feet' },
  { value: 'warm-feet', label: 'Warm Feet' },
  { value: 'cool-feet', label: 'Cool Feet' },
  
  // Accessories
  { value: 'barefoot', label: 'Barefoot' },
  { value: 'socks', label: 'Socks' },
  { value: 'stockings', label: 'Stockings' },
  { value: 'flip-flops', label: 'Flip Flops' },
  { value: 'heels', label: 'Heels' },
  { value: 'high-heels', label: 'High Heels' },
  { value: 'stilettos', label: 'Stilettos' },
  { value: 'flats', label: 'Flats' },
  { value: 'sandals', label: 'Sandals' },
  { value: 'boots', label: 'Boots' },
  
  // Special
  { value: 'pregnant-feet', label: 'Pregnant Feet' },
  { value: 'cosplay-feet', label: 'Cosplay Feet' },
  { value: 'roleplay', label: 'Roleplay' },
  { value: 'foot-worship', label: 'Foot Worship' },
  { value: 'custom-requests', label: 'Custom Requests' }
];

const BuildPayPiggyProfilePage = () => {
  const [formData, setFormData] = useState({
    bio: '',
    tags: [],
    dateOfBirth: '',
    gender: '',
    ageVerification: false
  });
  const [profilePic, setProfilePic] = useState(null);
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [profileComplete, setProfileComplete] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const api = useApiClient();
  const { me, refetchSession } = useSession();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTagsChange = (value) => {
    setFormData(prev => ({
      ...prev,
      tags: value
    }));
  };

  const handleProfilePicUpload = (file) => {
    if (file) {
      setProfilePic(URL.createObjectURL(file));
      setProfilePicFile(file);
    }
  };

  // The wizard used to throw everything away on submit. It now persists:
  // the picture (§5 upload), the profile fields (§4.2 PATCH) and the ToS
  // acceptance (§4.2, idempotent) before navigating.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (profilePicFile) {
        await uploadProfileImage(api, profilePicFile, 'profile');
      }
      await api.patch('/api/v1/me/profile', {
        bio: formData.bio,
        // Server clamps categories to 3 (§3.3).
        categories: formData.tags,
        date_of_birth: formData.dateOfBirth || null,
        gender: formData.gender || null,
      });
      // The 18+ confirmation is the ToS acceptance record (§4.2).
      await api.post('/api/v1/me/accept-terms');
      await refetchSession?.();

      setProfileComplete(true);
      setTimeout(() => {
        navigate('/seller/dashboard');
      }, 1500);
    } catch (err) {
      notifications.show({
        title: 'Could not save your profile',
        message: err?.message || 'Please try again',
        color: 'red'
      });
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = profilePic && formData.ageVerification && !saving;

  return (
    <Container size="sm" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="center">
          <Button 
            component={Link} 
            to="/piggy-subscription" 
            variant="subtle" 
            leftSection={<IconArrowLeft size="1rem" />}
          >
            Back to Membership
          </Button>
        </Group>

        <Center>
          <Stack gap="md" align="center" maw={400}>
            <Title order={1} ta="center">
              Create Your Profile
            </Title>
            <Text size="lg" ta="center" c="dimmed">
              Set up your Pay Piggy profile
            </Text>
          </Stack>
        </Center>

        <Card shadow="md" padding="xl" radius="md" withBorder>
          <form onSubmit={handleSubmit}>
            <Stack gap="xl">
              {/* Username Display */}
              <Center>
                <Text size="xl" fw={700} c="grape">
                  @{me?.display_name || 'you'}
                </Text>
              </Center>

              {/* Profile Picture - Only photo allowed */}
              <Stack gap="md" align="center">
                <Text fw={500}>Profile Picture <Text span c="red">*</Text></Text>
                <Box style={{ position: 'relative' }}>
                  {profilePic ? (
                    <Image 
                      src={profilePic} 
                      alt="Profile" 
                      w={150} 
                      h={150} 
                      radius="50%" 
                      style={{ border: '3px solid #9f38cc' }}
                    />
                  ) : (
                    <Box 
                      w={150} 
                      h={150} 
                      style={{ 
                        borderRadius: '50%', 
                        border: '3px dashed #ced4da',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f8f9fa'
                      }}
                    >
                      <IconCamera size={48} color="#adb5bd" />
                    </Box>
                  )}
                </Box>
                <FileButton onChange={handleProfilePicUpload} accept="image/*">
                  {(props) => (
                    <Button {...props} color="grape" variant={profilePic ? 'light' : 'filled'}>
                      {profilePic ? 'Change Photo' : 'Upload Photo'}
                    </Button>
                  )}
                </FileButton>
                {!profilePic && (
                  <Text size="sm" c="red" ta="center">
                    Profile picture is required
                  </Text>
                )}
              </Stack>

              {/* Tags */}
              <Stack gap="md">
                <Text fw={500}>Tags</Text>
                <Text size="sm" c="dimmed">
                  Select all the attributes that describe your feet (comma-separated)
                </Text>
                <MultiSelect
                  data={footAttributes}
                  value={formData.tags}
                  onChange={handleTagsChange}
                  placeholder="Select your foot attributes..."
                  searchable
                  clearable
                  maxDropdownHeight={400}
                  color="grape"
                />
              </Stack>

              {/* Bio */}
              <Stack gap="md">
                <Text fw={500}>Short Bio</Text>
                <Text size="sm" c="dimmed">
                  Write a short bio displayed under your username
                </Text>
                <Textarea
                  placeholder="Tell us about yourself..."
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  minRows={3}
                  maxRows={5}
                  maxLength={200}
                />
                <Text size="xs" c="dimmed" ta="right">
                  {formData.bio.length}/200
                </Text>
              </Stack>

              {/* Date of birth + gender (contract §3.3) */}
              <Stack gap="md">
                <TextInput
                  type="date"
                  label="Date of birth"
                  description="Only you can see this — it is never shown on your public profile."
                  value={formData.dateOfBirth}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => {
                    // Read the value BEFORE setFormData: React nulls
                    // currentTarget once the event finishes propagating, and
                    // the updater below runs later, during render.
                    const value = e.target.value;
                    setFormData((prev) => ({ ...prev, dateOfBirth: value }));
                  }}
                />
                <Select
                  label="Gender"
                  placeholder="Select..."
                  data={GENDER_OPTIONS}
                  value={formData.gender || null}
                  clearable
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, gender: value || '' }))
                  }
                />
              </Stack>

              {/* Age Verification Checkbox */}
              <Checkbox
                label="I confirm I am 18 years or older"
                checked={formData.ageVerification}
                onChange={(e) => setFormData(prev => ({ ...prev, ageVerification: e.target.checked }))}
                color="grape"
                required
              />

              {/* Submit Button */}
              <Button 
                type="submit" 
                fullWidth 
                size="lg" 
                color="grape"
                disabled={!canSubmit}
                loading={saving}
              >
                Complete Profile
              </Button>
            </Stack>
          </form>
        </Card>

        {/* Success Message */}
        {profileComplete && (
          <Card shadow="md" padding="xl" radius="md" withBorder>
            <Stack align="center" gap="md">
              <IconCheck size={48} color="var(--mantine-color-green-6)" />
              <Title order={2} ta="center">
                Profile Complete!
              </Title>
              <Text ta="center" c="dimmed">
                Redirecting to your dashboard...
              </Text>
            </Stack>
          </Card>
        )}
      </Stack>
    </Container>
  );
};

export default BuildPayPiggyProfilePage;